import { baseApiPath, deleteEntity, get, post, put } from "./base-api";
import {
  IPaymentHistoryRecord,
  IPlan,
  IPromoCodeAddResponse,
} from "../data-access";

const getPaymentsHistory = (): Promise<{
  data: {
    billingPayments: IPaymentHistoryRecord[];
    billingBotPayments: IPaymentHistoryRecord[];
  };
}> => {
  return get(`${baseApiPath}/api/billing/payments`);
};

const getPlans = (): Promise<{ data: IPlan[] }> => {
  return get(`${baseApiPath}/api/billing/plans`);
};

const selectPlan = (id: number): Promise<{ data: IPlan[] }> => {
  return post(`${baseApiPath}/api/billing/plans/${id}/select`, {});
};

const subscribe = (
  planId: number,
  interval: "monthly" | "yearly" = "monthly"
): Promise<{ checkout_url: string }> => {
  return post(`${baseApiPath}/api/billing/plans/${planId}/subscribe`, { interval });
};

const applyPromoCode = (code: string): Promise<IPromoCodeAddResponse> => {
  return post(`${baseApiPath}/api/billing/promocodes/add`, { code });
};

const cancelSubscription = (): Promise<{ message: string; cancel_at: string | null }> => {
  return post(`${baseApiPath}/api/billing/cancel-subscription`, {});
};

const previewPlanChange = (
  planId: number,
  interval: "monthly" | "yearly" = "monthly"
): Promise<{
  amount_due: number;
  credit: number;
  subtotal: number;
  tax: number;
  new_charge: number;
  proration_credit: number;
  current_plan_name: string | null;
  currency: string;
  new_plan: string;
  new_price: number;
  interval: string;
  seats: number;
  /** Legacy (Lite/Pro/Max) -> Flex: always an immediate upgrade, whatever the price. */
  is_flex_migration?: boolean;
  is_downgrade?: boolean;
  /** Unused legacy credits that move to the extra balance on a Flex migration. */
  credits_carry_over?: number;
}> => {
  return get(`${baseApiPath}/api/billing/plans/${planId}/preview-change?interval=${interval}`);
};

const changePlan = (
  planId: number,
  interval: "monthly" | "yearly" = "monthly"
): Promise<{ message: string; new_plan: string; downgrade?: boolean; effective_at?: string | null; credits_carried_over?: number }> => {
  return post(`${baseApiPath}/api/billing/plans/${planId}/change`, { interval });
};

const getCustomerPortal = (): Promise<{ url: string }> => {
  return get(`${baseApiPath}/api/billing/customer-portal`);
};

export interface UpcomingInvoice {
  amount: number;
  per_seat: number;
  seats: number;
  interval: "monthly" | "yearly";
  currency: string;
  next_payment_date: string | null;
  tax: number;
  total: number;
  pending_plan: { name: string; effective_at: string | null } | null;
  canceling: boolean;
  cancel_at: string | null;
}

const getUpcomingInvoice = (): Promise<UpcomingInvoice> => {
  return get(`${baseApiPath}/api/billing/upcoming-invoice`);
};

export interface PaymentMethod {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

export interface BillingAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface BillingTaxId {
  id: string;
  type: string;
  value: string;
}

export interface BillingInfo {
  name: string;
  is_company: boolean;
  address: BillingAddress;
  tax_id: BillingTaxId | null;
}

const getPaymentMethod = (): Promise<{
  payment_method: PaymentMethod | null;
  billing_info: BillingInfo | null;
  stripe_public_key: string;
}> => {
  return get(`${baseApiPath}/api/billing/payment-method`);
};

const createSetupIntent = (): Promise<{ client_secret: string }> => {
  return post(`${baseApiPath}/api/billing/payment-method/setup-intent`, {});
};

const setDefaultPaymentMethod = (paymentMethodId: string): Promise<{ message: string }> => {
  return post(`${baseApiPath}/api/billing/payment-method/default`, { payment_method_id: paymentMethodId });
};

const updateBillingInfo = (data: {
  name?: string;
  is_company?: boolean;
  address?: BillingAddress;
  tax_id?: { value: string } | null;
  old_tax_id?: string;
}): Promise<{ message: string }> => {
  return post(`${baseApiPath}/api/billing/billing-info`, data);
};

export interface CreditPack {
  credits: number;
  price: number;
  price_per_credit: number;
  discount: number;
  savings: number;
}

const getCreditPacks = (): Promise<{ packs: CreditPack[] }> => {
  return get(`${baseApiPath}/api/billing/credits/packs`);
};

export interface TeamMemberUsage {
  id: string;
  full_name: string | null;
  email: string;
  picture: string | null;
  paid_user: boolean;
  credits_used: number;
  proposals: number;
  answers: number;
}

const getTeamUsage = (): Promise<{ members: TeamMemberUsage[] }> => {
  return get(`${baseApiPath}/api/billing/credits/team-usage`);
};

export interface UsageHistoryRecord {
  id: string;
  user_name: string;
  user_picture: string | null;
  action: string;
  credits: number;
  source: string;
  created_at: string;
}

export interface UsageHistoryResponse {
  data: UsageHistoryRecord[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

const getUsageHistory = (page: number = 1, perPage: number = 10): Promise<UsageHistoryResponse> => {
  return get(`${baseApiPath}/api/billing/credits/usage-history?page=${page}&per_page=${perPage}`);
};

const purchaseCredits = (credits: number): Promise<{
  message: string;
  credits_added: number;
  ai_credits_extra: number;
}> => {
  return post(`${baseApiPath}/api/billing/credits/purchase`, { credits });
};

export interface IByokKeyState {
  has_key: boolean;
  key_last4: string | null;
  status: "valid" | "invalid" | "no_credits" | null;
}

const getByokKey = (): Promise<IByokKeyState & { enabled: boolean }> => {
  return get(`${baseApiPath}/api/billing/byok-key`);
};

const saveByokKey = (key: string): Promise<IByokKeyState> => {
  return put(`${baseApiPath}/api/billing/byok-key`, { key });
};

const deleteByokKey = (): Promise<IByokKeyState> => {
  return deleteEntity(`${baseApiPath}/api/billing/byok-key`);
};

export const billingApi = {
  getPaymentsHistory,
  getByokKey,
  saveByokKey,
  deleteByokKey,
  getPlans,
  selectPlan,
  subscribe,
  applyPromoCode,
  cancelSubscription,
  previewPlanChange,
  changePlan,
  getCustomerPortal,
  getUpcomingInvoice,
  getPaymentMethod,
  createSetupIntent,
  setDefaultPaymentMethod,
  updateBillingInfo,
  getCreditPacks,
  getTeamUsage,
  getUsageHistory,
  purchaseCredits,
};
